/**
 * Centralized Premium Access Hook
 * 
 * This hook provides a single source of truth for premium access logic.
 * Use this hook everywhere instead of checking user.plan or subscription_status directly.
 * 
 * @returns {Object} Premium access state and utilities
 */

import { useAuth } from '../contexts/AuthContext';

const usePremiumAccess = () => {
  const { user, loading } = useAuth();

  /**
   * Check if user has premium access
   * 
   * Premium access is granted if ANY of these conditions are true:
   * 1. User role is 'admin'
   * 2. User plan is 'premium'
   * 3. User subscription_status is 'active'
   * 4. User subscription_status is 'trialing'
   * 
   * @returns {boolean} True if user has premium access
   */
  const hasPremiumAccess = () => {
    if (!user) return false;
    
    // Admins always have full access
    if (user.role === 'admin') return true;
    
    // Check premium plan
    if (user.plan === 'premium') return true;
    
    // Check active subscription states
    const activeStatuses = ['active', 'trialing'];
    if (user.subscription_status && activeStatuses.includes(user.subscription_status)) {
      return true;
    }
    
    return false;
  };

  /**
   * Check if user's premium access is from a trial
   * @returns {boolean} True if user is in trial period
   */
  const isTrialing = () => {
    if (!user) return false;
    return user.subscription_status === 'trialing';
  };

  /**
   * Check if user's premium access is active (paid)
   * @returns {boolean} True if user has paid premium subscription
   */
  const isActivePremium = () => {
    if (!user) return false;
    return user.subscription_status === 'active' || user.plan === 'premium';
  };

  /**
   * Check if user is admin
   * @returns {boolean} True if user is admin
   */
  const isAdmin = () => {
    if (!user) return false;
    return user.role === 'admin';
  };

  /**
   * Get premium status details
   * @returns {Object} Detailed premium status
   */
  const getPremiumStatus = () => {
    if (!user) {
      return {
        hasPremium: false,
        isTrialing: false,
        isActive: false,
        isAdmin: false,
        status: 'not_logged_in'
      };
    }

    return {
      hasPremium: hasPremiumAccess(),
      isTrialing: isTrialing(),
      isActive: isActivePremium(),
      isAdmin: isAdmin(),
      status: user.subscription_status || 'inactive',
      plan: user.plan || 'free'
    };
  };

  return {
    // Primary check - use this in most cases
    hasPremiumAccess: hasPremiumAccess(),
    
    // Detailed checks
    isTrialing: isTrialing(),
    isActivePremium: isActivePremium(),
    isAdmin: isAdmin(),
    
    // Full status
    premiumStatus: getPremiumStatus(),
    
    // Loading state
    loading,
    
    // User object (for edge cases)
    user
  };
};

export default usePremiumAccess;
